import * as React from 'react';
import Menu from '@mui/material/Menu';
import StyledTooltip from '../StyledTooltip';
import { MoreActionsHorizontal } from '@sweep-io/sweep-design/dist/icons';
import { IconButton } from '@sweep-io/sweep-design';
import { SxProps } from '@mui/material';
import {
  IconButtonSize,
  IconButtonVariants,
} from '@sweep-io/sweep-design/dist/components/IconButton/types';
import { ActionsMenuItem } from './ActionsMenuItem';
import { PopoverOrigin } from '@mui/material/Popover';

const ITEM_HEIGHT = 48;

export interface ActionsMenuAction<ItemValueT = any> {
  label: string | React.ReactElement;
  value: ItemValueT;
  hidden?: boolean;
  disabled?: boolean;
  isToggle?: boolean;
  isToggleChecked?: boolean;
  onToggleChange?: (active: boolean) => any;
  actionIcon?: React.ReactElement;
  actionSx?: SxProps;
  isNested?: boolean;
  nestedTitle?: string;
  nestedActions?: ActionsMenuAction[];
  addBottomDivider?: boolean;
  isSelected?: boolean;
  disabledTooltip?: string;
}

const isActionVisible = (action: ActionsMenuAction) => !action.hidden;

export type MenuPlacement = 'left' | 'right' | 'bottom' | 'top';

export interface ActionsMenuProps<ItemValueT = any> {
  actions: ActionsMenuAction<ItemValueT>[];
  onClick?: (action: ActionsMenuAction<ItemValueT>) => any;
  MenuButtonIcon?: React.ReactElement;
  iconTooltip?: string;
  iconSize?: IconButtonSize;
  iconVariant?: IconButtonVariants;
  buttonTestId?: string;
  disabled?: boolean;
  menuPlacement?: MenuPlacement;
  menuWidth?: string;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  menuSx?: SxProps;
}

const getMenuProps = (
  menuPlacement?: MenuPlacement,
): {
  anchorOrigin: PopoverOrigin;
  transformOrigin: PopoverOrigin;
} => {
  switch (menuPlacement) {
    case 'left':
      return {
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      };
    case 'bottom':
      return {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
      };
    case 'top':
      return {
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
        transformOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      };
    case 'right':
    default:
      return {
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
      };
  }
};

export function ActionsMenu<ItemValueT = any>({
  actions,
  onClick,
  MenuButtonIcon,
  iconTooltip,
  iconSize = 'small',
  iconVariant = 'flat',
  buttonTestId = 'actions-menu-button',
  disabled,
  menuPlacement = 'right',
  anchorOrigin,
  transformOrigin,
  menuWidth = '268px',
  menuSx,
}: ActionsMenuProps<ItemValueT>) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuProps = getMenuProps(menuPlacement);
  if (anchorOrigin) {
    menuProps.anchorOrigin = anchorOrigin;
  }
  if (transformOrigin) {
    menuProps.transformOrigin = transformOrigin;
  }

  return (
    <div>
      {actions.length > 0 && (
        <>
          <StyledTooltip title={iconTooltip} placement="top">
            <span>
              <IconButton
                onClick={handleClick}
                variant={iconVariant}
                size={iconSize}
                data-testid={buttonTestId}
                disabled={disabled}
                pressed={open}
              >
                {MenuButtonIcon ?? <MoreActionsHorizontal />}
              </IconButton>
            </span>
          </StyledTooltip>

          <Menu
            sx={menuSx}
            disableAutoFocusItem
            id="long-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={menuProps.anchorOrigin}
            transformOrigin={menuProps.transformOrigin}
            slotProps={{
              paper: {
                style: {
                  maxHeight: ITEM_HEIGHT * 10,
                  width: menuWidth,
                },
              },
            }}
          >
            {actions.filter(isActionVisible).map((action) => (
              <ActionsMenuItem
                action={action}
                handleClose={handleClose}
                onClick={onClick}
                key={JSON.stringify(action.value)}
              />
            ))}
          </Menu>
        </>
      )}
    </div>
  );
}
